import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Star, Shield, CheckCircle, ChevronDown, ChevronUp, Calendar, ArrowRight, Bone, Dumbbell, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import LandingPageHeader from '../components/LandingPageHeader';
import { SEOHead, BreadcrumbSchema, FAQSchema, MedicalSpecialtySchema } from '../components/SEOHead';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrthopaedicsPage = () => {
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', treatment: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.treatment) {
      toast.error('Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/specialty-leads`, { ...formData, specialty: 'Orthopaedics', source_page: '/orthopaedics' });
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'form_submission', form_name: 'orthopaedics_lead', form_department: 'Orthopaedics', form_source: '/orthopaedics' });
      }
      if (window.gtag) { window.gtag('event', 'conversion', { send_to: 'AW-17542370694/orthopaedics_lead' }); }
      toast.success('Thank you! Our orthopaedics team will contact you shortly.');
      setFormData({ name: '', phone: '', email: '', treatment: '' });
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const treatments = [
    'Joint Replacement', 'Knee Pain', 'Hip Replacement', 'Arthroscopy',
    'Spine Surgery', 'Fracture Treatment', 'Sports Injury', 'Other',
  ];

  const services = [
    { title: 'Total Joint Replacement', desc: 'Advanced knee, hip & shoulder replacement surgery with imported implants for pain-free mobility', icon: Bone, color: '#f57c00' },
    { title: 'Arthroscopic Surgery', desc: 'Minimally invasive keyhole surgery for torn ligaments (ACL/PCL), meniscus repair & rotator cuff', icon: Shield, color: '#0066b3' },
    { title: 'Spine Surgery', desc: 'Microdiscectomy, spinal fusion, scoliosis correction, and minimally invasive spine procedures', icon: Shield, color: '#7b1fa2' },
    { title: 'Trauma & Fracture Care', desc: '24/7 trauma care with advanced fixation techniques for complex and compound fractures', icon: Shield, color: '#c41e3a' },
    { title: 'Sports Medicine', desc: 'Comprehensive treatment for sports injuries including ligament reconstruction and rehabilitation', icon: Dumbbell, color: '#00897b' },
    { title: 'Physiotherapy & Rehab', desc: 'Post-operative rehabilitation, pain management, and physiotherapy for faster recovery', icon: CheckCircle, color: '#0097a7' },
  ];

  const stats = [
    { value: '5,000+', label: 'Joint Replacements' },
    { value: '24/7', label: 'Trauma Care' },
    { value: 'NABH', label: 'Accredited' },
    { value: '40+', label: 'Years Legacy' },
  ];

  const trustPoints = [
    'Imported Joint Implants', 'Computer-Navigated Surgery', 'Dedicated Physiotherapy Unit',
    '24/7 Trauma & Emergency', 'Insurance & TPA Cashless', 'NABH Accredited Hospital',
  ];

  const testimonials = [
    { name: 'Suresh K.', location: 'Triplicane', rating: 5, text: 'Had my knee replacement done at Sakthi Hospital. I was walking within 2 days! The orthopaedic team is incredibly skilled and caring. No more knee pain after years of suffering.' },
    { name: 'Padma N.', location: 'Royapettah', rating: 5, text: 'My mother had a hip fracture and was treated at Sakthi Hospital. The surgery was successful and the recovery was faster than expected. The physiotherapy team was excellent.' },
    { name: 'Arjun R.', location: 'Mylapore', rating: 5, text: 'Sports injury to my ACL was treated arthroscopically. Minimal incision, quick recovery. The doctor explained everything clearly. Back on the field in 3 months!' },
  ];

  const faqs = [
    { q: 'When do I need a knee replacement?', a: 'Knee replacement is recommended when conservative treatments (medication, physiotherapy) no longer provide relief from severe knee pain, stiffness, and limited mobility. Our specialists will evaluate your condition with X-rays and assessments before recommending surgery.' },
    { q: 'How long is the recovery after joint replacement?', a: 'Most patients start walking with support within 1-2 days after surgery. Full recovery takes 6-12 weeks with regular physiotherapy. At Sakthi Hospital, our in-house rehab team ensures optimal recovery.' },
    { q: 'Do you treat sports injuries?', a: 'Yes, we treat all types of sports injuries including ACL/PCL tears, meniscus injuries, rotator cuff tears, tennis elbow, and stress fractures using advanced arthroscopic techniques and sports medicine protocols.' },
    { q: 'What types of implants do you use?', a: 'We use internationally certified, high-quality imported implants from leading manufacturers. The choice of implant is tailored to each patient based on age, activity level, and bone quality for the best outcomes.' },
    { q: 'Is physiotherapy included in the treatment?', a: 'Yes, Sakthi Hospital has a dedicated physiotherapy and rehabilitation unit. Post-surgical rehab is an integral part of our orthopaedic treatment plans to ensure complete recovery and return to normal activities.' },
  ];

  const LeadForm = ({ className = '' }) => (
    <form onSubmit={handleSubmit} className={className}>
      <input type="hidden" id="zc_gad" name="zc_gad" value="" />
      <div className="space-y-3">
        <div>
          <Label className="text-sm font-medium text-gray-700">Your Name</Label>
          <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Full Name" className="mt-1 h-11" data-testid="ortho-form-name" />
        </div>
        <div>
          <Label className="text-sm font-medium text-gray-700">Phone Number</Label>
          <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+91 98402 64444" className="mt-1 h-11" data-testid="ortho-form-phone" />
        </div>
        <div>
          <Label className="text-sm font-medium text-gray-700">Email Address</Label>
          <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="your@email.com" className="mt-1 h-11" data-testid="ortho-form-email" />
        </div>
        <div>
          <Label className="text-sm font-medium text-gray-700">Select Concern</Label>
          <Select value={formData.treatment} onValueChange={(v) => setFormData({...formData, treatment: v})}>
            <SelectTrigger className="mt-1 h-11" data-testid="ortho-form-treatment"><SelectValue placeholder="Select Concern" /></SelectTrigger>
            <SelectContent>{treatments.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={loading} className="w-full h-12 bg-[#f57c00] hover:bg-[#e06800] text-white font-semibold rounded-lg" data-testid="ortho-form-submit">
          {loading ? <><Loader2 size={18} className="animate-spin mr-2" /> Submitting...</> : <><Calendar size={18} className="mr-2" /> Book Consultation</>}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="orthopaedics-page">
      <SEOHead
        title="Best Orthopaedic Hospital in Chennai - Joint Replacement & Bone Care | Sakthi Hospital"
        description="Expert orthopaedic care at Sakthi Hospital Chennai. Knee & hip replacement, arthroscopy, spine surgery, fracture treatment, sports injury. Imported implants. Physiotherapy. NABH Accredited. Book free consultation."
        path="/orthopaedics"
        keywords="orthopaedic hospital Chennai, knee replacement Triplicane, hip replacement Chennai, best orthopaedic doctor, joint replacement surgery, arthroscopy Chennai, spine surgery, fracture treatment, sports injury treatment Chennai, physiotherapy hospital"
      />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }, { name: 'Orthopaedics', path: '/orthopaedics' }]} />
      <FAQSchema questions={faqs} />
      <MedicalSpecialtySchema
        name="Orthopaedics"
        description="Advanced orthopaedic and joint care at Sakthi Hospital Chennai. Total knee and hip replacement, arthroscopy, spine surgery, trauma care, and in-house physiotherapy rehabilitation."
        procedures={['Total Knee Replacement', 'Hip Replacement', 'Shoulder Replacement', 'ACL Reconstruction', 'Arthroscopy', 'Spine Surgery', 'Fracture Fixation', 'Sports Injury Treatment', 'Physiotherapy']}
      />
      <LandingPageHeader accentColor="#f57c00" subtitle="Orthopaedics & Joint Care" />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#fff8f0] via-white to-[#fff3e0]" data-testid="ortho-hero">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#f57c00]/5 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="space-y-6">
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-[#f57c00]/10 text-[#f57c00] rounded-full text-xs font-semibold">Advanced Joint Care</span>
                <span className="px-3 py-1 bg-[#f57c00]/10 text-[#f57c00] rounded-full text-xs font-semibold">NABH Accredited</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0f172a] leading-[1.1] tracking-tight" data-testid="ortho-headline">
                Move Without Pain.{' '}
                <span className="text-[#f57c00]">Live Without Limits.</span>
                <span className="block text-lg sm:text-xl font-semibold text-[#64748b] mt-3">Expert Orthopaedic Care at Sakthi Hospital</span>
              </h1>
              <p className="text-base text-[#5a6b7c] leading-relaxed max-w-lg">
                From joint replacements to sports injuries — our experienced orthopaedic surgeons restore mobility with advanced surgical techniques, imported implants, and comprehensive rehabilitation.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="tel:+919840264444" className="bg-[#f57c00] text-white px-7 py-3.5 rounded-lg font-semibold shadow-lg shadow-[#f57c00]/25 hover:bg-[#e06800] transition-all flex items-center gap-2" data-testid="ortho-call-btn">
                  <Phone size={18} /> Call Now
                </a>
                <a href="#ortho-booking" className="bg-white text-[#f57c00] border-2 border-[#f57c00] px-7 py-3.5 rounded-lg font-semibold hover:bg-[#f57c00] hover:text-white transition-all flex items-center gap-2">
                  <Calendar size={18} /> Free Consultation
                </a>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-orange-100">
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold text-[#0f172a]">Get Expert Opinion</h3>
                <p className="text-sm text-[#64748b]">Free consultation & treatment cost estimate</p>
              </div>
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#f57c00] py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            {stats.map((s) => (<div key={s.value}><div className="text-3xl font-extrabold">{s.value}</div><div className="text-sm opacity-80 mt-1">{s.label}</div></div>))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-3">Complete Bone & Joint Care</h2>
            <p className="text-[#64748b] max-w-xl mx-auto">Advanced surgical and non-surgical treatments for all orthopaedic conditions</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <div key={s.title} className="bg-gradient-to-br from-[#fff8f0] to-white rounded-2xl p-6 border border-orange-100 hover:shadow-lg transition-all" data-testid={`ortho-service-${s.title}`}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${s.color}15`, color: s.color }}><s.icon size={28} /></div>
                <h3 className="text-lg font-bold text-[#0f172a] mb-2">{s.title}</h3>
                <p className="text-sm text-[#5a6b7c] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Points */}
      <section className="py-16 bg-gradient-to-b from-[#fff8f0] to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10"><h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-3">Why Choose Our Orthopaedics Department</h2></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {trustPoints.map((point) => (
              <div key={point} className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm border border-orange-50">
                <CheckCircle size={22} className="text-[#f57c00] flex-shrink-0" /><span className="font-medium text-[#0f172a]">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10"><h2 className="text-3xl md:text-4xl font-bold text-[#0f172a]">Patient Success Stories</h2></div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-gradient-to-br from-[#fff8f0] to-white rounded-2xl p-6 shadow-md border border-orange-50" data-testid={`ortho-testimonial-${t.name}`}>
                <div className="flex gap-0.5 mb-3">{[...Array(t.rating)].map((_, j) => <Star key={`star-${j}`} size={16} fill="#d4a24c" stroke="#d4a24c" />)}</div>
                <p className="text-sm text-[#3a4a5c] leading-relaxed mb-4 italic">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className="w-10 h-10 bg-[#f57c00] rounded-full flex items-center justify-center text-white font-bold text-sm">{t.name[0]}</div>
                  <div><div className="font-semibold text-sm text-[#0f172a]">{t.name}</div><div className="text-xs text-[#94a3b8]">{t.location}, Chennai</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gradient-to-b from-[#fff8f0] to-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#0f172a] mb-10">FAQs - Bones, Joints & Orthopaedic Care</h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={faq.q} className="border border-orange-100 rounded-xl overflow-hidden" data-testid={`ortho-faq-${idx}`}>
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between p-5 text-left hover:bg-[#fff8f0] transition-colors">
                  <span className="font-semibold text-[#0f172a] pr-4">{faq.q}</span>
                  {openFaq === idx ? <ChevronUp size={20} className="text-[#f57c00] flex-shrink-0" /> : <ChevronDown size={20} className="text-[#94a3b8] flex-shrink-0" />}
                </button>
                {openFaq === idx && <div className="px-5 pb-5 text-sm text-[#5a6b7c] leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#0f172a] mb-8">Visit Our Hospital</h2>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden h-72 shadow-lg">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.0!2d80.2757397!3d13.0570045!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a526605d0a030d5%3A0x6e079bd99529d311!2sSakthi%20Hospital!5e0!3m2!1sen!2sin!4v1704067200000!5m2!1sen!2sin" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Sakthi Hospital Location"></iframe>
            </div>
            <div className="flex flex-col justify-center space-y-4">
              <div className="flex items-start gap-3"><MapPin size={20} className="text-[#f57c00] mt-1 flex-shrink-0" /><div><p className="font-semibold text-[#0f172a]">Sakthi Hospital & Research Centre</p><p className="text-sm text-[#5a6b7c]">175, Big Street, Triplicane, Chennai - 600005</p></div></div>
              <div className="flex items-center gap-3"><Phone size={20} className="text-[#f57c00] flex-shrink-0" /><p className="text-sm text-[#5a6b7c]">+91 44 2844 2626 | +91 98402 64444</p></div>
              <div className="flex items-center gap-3"><Clock size={20} className="text-[#f57c00] flex-shrink-0" /><p className="text-sm text-[#5a6b7c]">OPD: 9:00 AM - 9:00 PM | Emergency: 24/7</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="ortho-booking" className="py-16 bg-[#f57c00]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="text-white space-y-4">
              <h2 className="text-3xl font-bold">Joint Pain Holding You Back? Get Expert Care Today.</h2>
              <p className="opacity-90">Book your free orthopaedic consultation. Walk pain-free again.</p>
              <div className="flex flex-wrap gap-3">
                <a href="tel:+919840264444" className="bg-white text-[#f57c00] px-6 py-3 rounded-lg font-semibold flex items-center gap-2 hover:bg-gray-50 transition-colors"><Phone size={18} /> Call Now</a>
                <a href="https://wa.me/919840264444" target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 hover:bg-[#20bd5a] transition-colors">WhatsApp Now</a>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-xl">
              <h3 className="text-lg font-bold text-center text-[#0f172a] mb-4">Book Free Consultation</h3>
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      <div className="bg-[#0f172a] text-white py-6 text-center">
        <Link to="/" className="text-sm text-white/70 hover:text-[#f57c00] transition-colors flex items-center gap-2 justify-center"><ArrowRight size={14} className="rotate-180" /> Back to Sakthi Hospital Home</Link>
        <p className="text-xs text-white/50 mt-2">© 2025 Sakthi Hospital & Research Centre. All Rights Reserved.</p>
      </div>
    </div>
  );
};

export default OrthopaedicsPage;
